import React, { FC, ReactNode, useState } from 'react';
import './ProfileSection.scss';

import { Button } from '../../../../../components/Button/Button';
import { ReactComponent as CloseEditIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/close-edit.svg';
import { ReactComponent as EditIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/edit.svg';

interface ProfileSectionProps {
  title: string;
  children: ReactNode; // View mode content
  editContent?: ReactNode; // Edit mode content
  isEditing?: boolean;
  isSaving?: boolean;
  onEditToggle?: (isEditing: boolean) => void;
  onSave?: () => void;
  onCancel?: () => void;
  headerAction?: ReactNode; // For things like "+ Add Certificate"
  isActionDisabled?: boolean; // Disable save button during uploads etc.
  className?: string;
}

export const ProfileSection: FC<ProfileSectionProps> = ({
  title,
  children,
  editContent,
  isEditing: externalIsEditing,
  isSaving = false,
  onEditToggle,
  onSave,
  onCancel,
  headerAction,
  isActionDisabled = false,
  className = '',
}) => {
  const [internalIsEditing, setInternalIsEditing] = useState(false);

  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;

  const handleEditClick = () => {
    const newState = true;
    setInternalIsEditing(newState);
    if (onEditToggle) onEditToggle(newState);
  };

  const handleCancelClick = () => {
    const newState = false;
    setInternalIsEditing(newState);
    if (onCancel) onCancel();
    if (onEditToggle) onEditToggle(newState);
  };

  const handleSaveClick = () => {
    if (onSave) {
      onSave();
    } else {
      const newState = false;
      setInternalIsEditing(newState);
      if (onEditToggle) onEditToggle(newState);
    }
  };

  return (
    <div className={`profile-section ${isEditing ? 'editing' : ''}`}>
      <div className={`section-header ${className}`}>
        <h2>{title}</h2>
        <div className="header-actions">
          {isEditing && headerAction && <div className="custom-action">{headerAction}</div>}
          {!isEditing && editContent && (
            <button className="edit-btn" onClick={handleEditClick} aria-label="Edit">
              {<EditIcon />}
            </button>
          )}
          {isEditing && (
            <button className="edit-btn active" aria-label="Edit" onClick={handleCancelClick}>
              {<CloseEditIcon />}
            </button>
          )}
        </div>
      </div>

      <div className="section-content">{isEditing && editContent ? editContent : children}</div>

      {isEditing && editContent && (
        <div className="section-footer">
          <button className="cancel-btn" onClick={handleCancelClick} disabled={isSaving}>
            {'Cancel'}
          </button>
          <Button
            className="save-btn"
            onClick={handleSaveClick}
            isLoading={isSaving}
            disabled={isSaving || isActionDisabled}
          >
            {'Save Changes'}
          </Button>
        </div>
      )}
    </div>
  );
};
